/**
 * `SkillList` — pagina con la lista ordinabile e paginata delle skill dello User
 * e i voti (Self / AI / Reviewer / Manager).
 *
 * Comportamento (Req. 10.1–10.5):
 *  - presenta le skill dello User (10.1), recuperate da `GET /api/assessments/me`
 *    via `useApiClient` + TanStack Query; con il filtro "Tutte le competenze" o
 *    una competenza specifica mostra anche le skill non ancora valutate;
 *  - la tabella e la valutazione al volo del Self sono delegate al componente
 *    condiviso {@link SkillAssessmentTable}, usato anche da `CompetenceDetail`;
 *  - la pagina resta responsabile del recupero dati, del filtro (voce del
 *    select + ricerca testuale) e del layout.
 *
 * *Validates: Requirements 7.4, 7.5, 7.6, 10.1, 10.2, 10.3, 10.4, 10.5.*
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  AssessmentView,
  CompetenceDto,
  DepartmentDto,
  SkillDto,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { useAuth } from '../auth/AuthProvider';
import { useAigor } from '../components/AigorDrawer';
import { SkillAssessmentTable } from '../components/SkillAssessmentTable';
import { Button, Field, PageHeader, TextInput } from '../components/ui';

/**
 * Valore del filtro (voce del select):
 * - `'mine'`: solo le skill di cui l'utente ha un assessment (le mie skill);
 * - `'unrated'`: solo le skill senza alcuna fonte di Livello (Livello_Self, AI,
 *   voto Reviewer e voto Manager tutti assenti), ovvero quelle ancora da
 *   valutare (Score_Skill assente);
 * - `'all'`: tutte le skill del catalogo (tutte le competenze);
 * - `<id>`: solo le skill della competenza scelta.
 */
export type CompetenceFilter = 'mine' | 'unrated' | 'all' | string;

/**
 * Filtra gli assessment secondo la voce del select e la query testuale
 * (case-insensitive) sul nome della skill.
 *
 * - `filter === 'mine'`: mantiene solo le skill di cui l'utente ha un
 *   assessment (`mySkillIds`).
 * - `filter === 'unrated'`: mantiene solo le skill senza alcuna fonte di Livello
 *   (Livello_Self, AI, voto Reviewer e voto Manager tutti assenti), a
 *   prescindere dalla competenza. Una skill priva di fonti non ha un assessment
 *   registrato, quindi `mySkillIds` non la conterrebbe: per questo non si
 *   applica quel vincolo.
 * - `filter === 'all'`: nessun filtro sulla competenza.
 * - `filter === <id>`: mantiene solo le skill della competenza scelta.
 *   L'appartenenza è determinata da `skillIdsByCompetence`, ovvero l'insieme
 *   delle skill collegate a ciascuna competenza (relazione N:N).
 *
 * Il filtro testuale è applicato dopo: la ricerca opera quindi all'interno
 * della selezione corrente.
 */
export function filterSkills(
  rows: AssessmentView[],
  query: string,
  filter: CompetenceFilter = 'mine',
  skillIdsByCompetence?: Map<string, Set<string>>,
  mySkillIds?: Set<string>,
): AssessmentView[] {
  let result = rows;

  if (filter === 'mine') {
    result = mySkillIds ? result.filter((r) => mySkillIds.has(r.skillId)) : [];
  } else if (filter === 'unrated') {
    // Skill senza alcuna fonte di Livello: né Self dichiarato, né AI, né voto
    // reviewer, né voto manager. Non ci si limita alle skill "possedute"
    // (`mySkillIds`): una skill priva di fonti non ha un assessment registrato,
    // quindi non comparirebbe. Assenza di tutte le fonti ⇒ Score_Skill assente
    // (skill non valutata).
    result = result.filter(
      (r) =>
        r.livelloSelf === null &&
        r.aiLevel === null &&
        r.reviewerLevelRank === null &&
        r.managerLevelRank === null,
    );
  } else if (filter !== 'all') {
    const allowed = skillIdsByCompetence?.get(filter);
    result = allowed ? result.filter((r) => allowed.has(r.skillId)) : [];
  }

  const q = query.trim().toLowerCase();
  if (q.length === 0) return result;
  return result.filter((r) => r.skillName.toLowerCase().includes(q));
}

/**
 * Pagina lista skill: recupera gli assessment dello User e li presenta con la
 * tabella condivisa (ordinabile, paginata, con valutazione al volo del Self).
 */
export default function SkillList() {
  const api = useApiClient();
  const { me } = useAuth();
  const { open: openAigor } = useAigor();
  const navigate = useNavigate();

  // Colonna AI e tasto AIgor visibili solo se l'AI è abilitata (Req. 7.4).
  const aiEnabled = me?.aiEnabled === true;

  const { data, isLoading, isError, error } = useQuery<AssessmentView[]>({
    queryKey: ['assessments', 'me'],
    queryFn: () => api.get<AssessmentView[]>('/api/assessments/me'),
  });

  // Catalogo competenze: popola il filtro integrato nella ricerca e determina
  // quali skill appartengono a ciascuna competenza (relazione N:N).
  const competencesQuery = useQuery<CompetenceDto[]>({
    queryKey: ['competences'],
    queryFn: () => api.get<CompetenceDto[]>('/api/competences'),
  });

  // Catalogo completo delle skill: consente di mostrare anche le skill non
  // ancora valutate (voti assenti) quando il filtro "Le mie skill" è disattivo.
  const skillsQuery = useQuery<SkillDto[]>({
    queryKey: ['skills'],
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
  });

  // Reparti: mappa id → nome per raggruppare le competenze del select per
  // reparto (optgroup).
  const departmentsQuery = useQuery<DepartmentDto[]>({
    queryKey: ['departments'],
    queryFn: () => api.get<DepartmentDto[]>('/api/departments'),
  });

  // Testo di ricerca per filtrare le skill per nome.
  const [search, setSearch] = useState('');

  // Voce del select integrato nel campo di ricerca. Default `'mine'` (le mie
  // skill); `'all'` = tutte le competenze; `<id>` = una competenza specifica.
  const [competenceFilter, setCompetenceFilter] = useState<CompetenceFilter>('mine');

  const assessments = useMemo(() => data ?? [], [data]);
  const competences = useMemo(
    () => competencesQuery.data ?? [],
    [competencesQuery.data],
  );
  const skills = useMemo(() => skillsQuery.data ?? [], [skillsQuery.data]);

  // Mappa skillId → SkillDto: serve a sapere quali skill sono autovalutabili /
  // valutabili con AI (per mostrare i tasti giusti nella colonna Azioni).
  const skillById = useMemo(() => new Map(skills.map((s) => [s.id, s])), [skills]);

  // Insieme delle skill "possedute" dall'utente (con un assessment registrato).
  const mySkillIds = useMemo(
    () => new Set(assessments.map((a) => a.skillId)),
    [assessments],
  );

  // Righe complete: l'intero catalogo skill, con i voti dell'utente quando
  // presenti (altrimenti assenti). Le skill valutate ma non più in catalogo
  // (dato orfano) sono comunque incluse per non perdere i voti dell'utente.
  const rows = useMemo<AssessmentView[]>(() => {
    const assessmentBySkill = new Map(assessments.map((a) => [a.skillId, a]));
    const merged: AssessmentView[] = skills.map((s) => {
      const existing = assessmentBySkill.get(s.id);
      if (existing) return existing;
      return {
        skillId: s.id,
        skillName: s.name,
        selfInterest: null,
        aiLevel: null,
        reviewerLevelRank: null,
        reviewerCount: 0,
        reviewerUniformLevel: null,
        managerLevelRank: null,
        managerCount: 0,
        managerUniformLevel: null,
        livelloSelf: null,
        yearsOfExperience: null,
        score: null,
        // Skill non valutata: nessuna certificazione contribuisce allo Score.
        certificationCount: 0,
        // Skill non valutata: nessuna revisione.
        reviews: [],
      };
    });
    // Aggiunge eventuali assessment su skill non presenti nel catalogo.
    const catalogIds = new Set(skills.map((s) => s.id));
    for (const a of assessments) {
      if (!catalogIds.has(a.skillId)) merged.push(a);
    }
    return merged;
  }, [skills, assessments]);

  // Mappa competenceId → insieme delle skill collegate, per il filtro.
  const skillIdsByCompetence = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const c of competences) {
      map.set(c.id, new Set(c.skillIds));
    }
    return map;
  }, [competences]);

  // Competenze raggruppate per reparto per l'optgroup del select: reparti in
  // ordine alfabetico e, dentro ciascuno, competenze in ordine alfabetico. Le
  // competenze senza reparto (o con reparto non più esistente) confluiscono nel
  // gruppo "Senza reparto", posto in coda.
  const competenceGroups = useMemo(() => {
    const departmentNameById = new Map(
      (departmentsQuery.data ?? []).map((d) => [d.id, d.name]),
    );
    const ORPHAN = '__none__';

    // Raggruppa per chiave reparto (id o ORPHAN) preservando i doc competenza.
    const byDepartment = new Map<string, CompetenceDto[]>();
    for (const c of competences) {
      const key =
        c.departmentId !== null && departmentNameById.has(c.departmentId)
          ? c.departmentId
          : ORPHAN;
      const list = byDepartment.get(key) ?? [];
      list.push(c);
      byDepartment.set(key, list);
    }

    const collator = new Intl.Collator('it', { sensitivity: 'base' });
    const groups = [...byDepartment.entries()].map(([key, items]) => ({
      key,
      label: key === ORPHAN ? 'Senza reparto' : departmentNameById.get(key) ?? 'Senza reparto',
      isOrphan: key === ORPHAN,
      competences: [...items].sort((a, b) => collator.compare(a.name, b.name)),
    }));

    // Reparti in ordine alfabetico; "Senza reparto" sempre in coda.
    groups.sort((a, b) => {
      if (a.isOrphan !== b.isOrphan) return a.isOrphan ? 1 : -1;
      return collator.compare(a.label, b.label);
    });
    return groups;
  }, [competences, departmentsQuery.data]);

  // Righe filtrate dalla voce del select e dalla ricerca testuale.
  const filteredRows = useMemo(
    () =>
      filterSkills(rows, search, competenceFilter, skillIdsByCompetence, mySkillIds),
    [rows, search, competenceFilter, skillIdsByCompetence, mySkillIds],
  );

  return (
    <section className="skill-list" aria-labelledby="skill-list-title">
      <PageHeader
        title="Le mie skill"
        titleId="skill-list-title"
        subtitle="Consulta il tuo Self e la media dei voti Reviewer e Manager per ogni skill."
        actions={
          <Button
            type="button"
            variant="primary"
            onClick={() => navigate('/assessment/skills/wizard')}
          >
            Inizia valutazione
          </Button>
        }
      />

      <Field
        id="skill-search"
        label="Cerca skill"
        hint="Scegli le tue skill, tutte o una competenza. La ricerca opera nella selezione corrente."
      >
        <div className="competence-search">
          <span className="competence-search__department user-admin__select">
            <select
              id="skill-search-competenza"
              aria-label="Filtra skill"
              value={competenceFilter}
              disabled={competencesQuery.isLoading}
              onChange={(e) => setCompetenceFilter(e.target.value)}
            >
              <optgroup label="Generale">
                <option value="mine">Le mie skill</option>
                <option value="unrated">Non valutate</option>
                <option value="all">Tutte le competenze</option>
              </optgroup>
              {competenceGroups.map((group) => (
                <optgroup key={group.key} label={group.label}>
                  {group.competences.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </span>
          <TextInput
            id="skill-search"
            className="competence-search__input"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca una skill…"
            autoComplete="off"
          />
        </div>
      </Field>

      <SkillAssessmentTable
        rows={filteredRows}
        aiEnabled={aiEnabled}
        skillById={skillById}
        onOpenAigor={(row) =>
          openAigor(row.skillId, {
            skillName: row.skillName,
            skillDescription: skillById.get(row.skillId)?.description,
            userName: me?.displayName,
            existingAiLevel: row.aiLevel,
          })
        }
        loading={isLoading}
        error={
          isError
            ? error instanceof Error
              ? error.message
              : 'Impossibile caricare la lista delle skill.'
            : null
        }
        emptyMessage={
          competenceFilter === 'mine'
            ? 'Non hai ancora skill valutate. Scegli "Tutte le competenze" per vedere il catalogo.'
            : competenceFilter === 'unrated'
              ? 'Nessuna skill senza voti: tutte le tue skill hanno almeno un voto.'
              : 'Nessuna skill corrisponde alla competenza o alla ricerca.'
        }
        defaultSort={{ key: 'skillName', direction: 'asc' }}
        pageSize={10}
      />
    </section>
  );
}
